import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DollarSign, CalendarCheck, Users, UserPlus, Plus, Trash2, Crown, ArrowUpRight } from "lucide-react";
import { api, fmtMoney, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const QUICK = [
  { to: "/admin", label: "Bookings" },
  { to: "/admin/availability", label: "Availability" },
  { to: "/admin/studio-hours", label: "Studio Hours" },
  { to: "/admin/content", label: "Content" },
  { to: "/admin/equipment", label: "Equipment" },
  { to: "/admin/promos", label: "Promos" },
  { to: "/admin/about", label: "About" },
  { to: "/admin/customers", label: "Customers" },
];

export default function SuperAdmin() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(null);
  const { data: ov } = useQuery({ queryKey: ["overview"], queryFn: () => api.get("/admin/overview").then((r) => r.data) });
  const { data: admins = [] } = useQuery({ queryKey: ["admins"], queryFn: () => api.get("/admin/admins").then((r) => r.data) });

  const createAdmin = async () => {
    try {
      await api.post("/admin/admins", adding);
      qc.invalidateQueries({ queryKey: ["admins"] });
      setAdding(null);
      toast.success("Admin created");
    } catch (e) { toast.error(apiError(e)); }
  };
  const delAdmin = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try {
      await api.delete(`/admin/admins/${id}`);
      qc.invalidateQueries({ queryKey: ["admins"] });
      toast.success("Removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  const stats = [
    { icon: DollarSign, label: "Revenue", value: fmtMoney(ov?.revenue_cents || 0) },
    { icon: CalendarCheck, label: "Paid Bookings", value: ov?.total_paid_bookings ?? 0 },
    { icon: CalendarCheck, label: "Upcoming", value: ov?.upcoming_appointments ?? 0 },
    { icon: Users, label: "Customers", value: ov?.total_customers ?? 0 },
    { icon: UserPlus, label: "New (7d)", value: ov?.new_customers_7d ?? 0 },
  ];

  return (
    <div data-testid="admin-super">
      <h1 className="font-display text-3xl text-white mb-1 flex items-center gap-2"><Crown className="text-[#D4AF37]" /> Super Admin</h1>
      <p className="text-white/50 text-sm mb-6">Business overview and team management.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
        {stats.map((s, i) => (
          <div key={i} className="bg-[#111] border border-white/10 rounded-2xl p-5" data-testid={`stat-${i}`}>
            <s.icon className="text-[#D4AF37] mb-3" size={20} />
            <p className="font-display text-2xl text-white">{s.value}</p>
            <p className="text-white/40 text-xs uppercase tracking-wide font-mono-label mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl text-white mb-3">Quick Links</h2>
      <div className="flex flex-wrap gap-2 mb-10">
        {QUICK.map((q) => (
          <Link key={q.to} to={q.to} className="border border-white/15 rounded-full px-4 py-2 text-sm text-white/70 hover:border-[#D4AF37] hover:text-white flex items-center gap-1.5">
            {q.label} <ArrowUpRight size={13} />
          </Link>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl text-white">Team Accounts</h2>
        <button onClick={() => setAdding({ email: "", password: "", name: "" })} data-testid="add-admin-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2"><Plus size={15} /> New Admin</button>
      </div>
      <div className="space-y-2 max-w-2xl">
        {admins.map((a, i) => (
          <div key={a.id} className="flex items-center justify-between bg-[#111] border border-white/10 rounded-xl px-5 py-3" data-testid={`admin-account-${i}`}>
            <div>
              <p className="text-white text-sm flex items-center gap-2">{a.name || a.email} {a.role === "super_admin" && <Crown size={13} className="text-[#D4AF37]" />}</p>
              <p className="text-white/40 text-xs">{a.email} · {a.role}</p>
            </div>
            {a.role !== "super_admin" && <button onClick={() => delAdmin(a.id)} data-testid={`delete-admin-${i}`} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>

      <Dialog open={!!adding} onOpenChange={(o) => !o && setAdding(null)}>
        <DialogContent className="bg-[#111] border-white/15 text-white">
          <DialogHeader><DialogTitle className="font-display">New Admin Account</DialogTitle></DialogHeader>
          {adding && (
            <div className="space-y-3">
              <Input placeholder="Name" value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="new-admin-name" />
              <Input placeholder="Email" value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="new-admin-email" />
              <Input type="password" placeholder="Password" value={adding.password} onChange={(e) => setAdding({ ...adding, password: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="new-admin-password" />
              <button onClick={createAdmin} data-testid="save-admin-btn" className="btn-gold w-full rounded-full py-3 text-sm font-mono-label uppercase">Create</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
